import { ItemCreatedUpdatedInfoView } from "@/components/views/ItemCreatedUpdatedInfoView";
import { selectStandardProps } from "@/utils/componentUtils";
import { formatPhoneNumber } from "@/utils/formatUtils";
import { phoneNumberNormalizeFunction, resolveFormValidationError, setErrorsToForm } from "@/utils/formUtils";
import { validations } from "@/utils/validationUtils";
import { Col, Form, Input, Modal, Row, Select } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useCreateDashboardContact, useUpdateDashboardContact } from "../../../../common/api/queries";
import HiddenInput from "../../../../common/components/form/components/HiddenInput";
import { ModalSizes, rowGutter } from "../../../../common/constants";
import { AgentSelect } from "../../../enumerations/components/form/AgentSelect";
import InstitutionSelect from "../../../enumerations/components/form/InstitutionSelect";
import { DashboardContactType } from "../../enums";
import { CreateUpdateDashboardContact, DashboardContact } from "../../types";
import { convertDashboardContactToCreateUpdateObject } from "../../utils";

interface Props {
  open: boolean;
  contact?: DashboardContact;
  onFormCancel: () => void;
}

export const DashboardContactForm = ({ open, contact, onFormCancel }: Props) => {
  const { t } = useTranslation();

  const [form] = Form.useForm<CreateUpdateDashboardContact>();
  const contactType = Form.useWatch("type", form);

  const createDashboardContact = useCreateDashboardContact();
  const updateDashboardContact = useUpdateDashboardContact();

  useEffect(() => {
    if (open && contact) {
      const formData = convertDashboardContactToCreateUpdateObject(contact);
      form.setFieldsValue(formData);
    }
  }, [open, contact, form]);

  const handleFormSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        if (contact) {
          updateDashboardContact.mutate(
            { id: contact.id, object: values },
            {
              onSuccess: onFormCancel,
              onError: (error) => setErrorsToForm(form, "dashboard.contacts.attrs", error.violations),
            },
          );
        } else {
          createDashboardContact.mutate(values, {
            onSuccess: onFormCancel,
            onError: (error) => setErrorsToForm(form, "dashboard.contacts.attrs", error.violations),
          });
        }
      })
      .catch(resolveFormValidationError);
  };

  return (
    <Modal
      width={ModalSizes.MEDIUM}
      open={open}
      title={t(`dashboard.contacts.titles.${contact ? "update" : "create"}`)}
      okText={t("common.save")}
      cancelText={t("common.cancel")}
      confirmLoading={createDashboardContact.isPending || updateDashboardContact.isPending}
      onOk={handleFormSubmit}
      onCancel={onFormCancel}
      afterClose={form.resetFields}
      mask={{ closable: false }}
    >
      <ItemCreatedUpdatedInfoView className="margin-bottom-small" item={contact} />

      <Form form={form} layout="vertical" name="dashboardContactForm">
        <HiddenInput name="optimisticLockVersion" />

        <Row gutter={rowGutter}>
          <Col span={12}>
            <Form.Item name="type" label={t("dashboard.contacts.enums.type._label")} rules={[validations.notNull]}>
              <Select
                {...selectStandardProps}
                options={Object.values(DashboardContactType).map((type) => ({
                  value: type,
                  label: t(`dashboard.contacts.enums.type.${type}`),
                }))}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name="position"
              label={t("dashboard.contacts.attrs.position")}
              rules={[validations.notBlank, validations.size(1, 255)]}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>

        {contactType === DashboardContactType.INTERNAL ? (
          <Row gutter={rowGutter}>
            <Col span={12}>
              <Form.Item name="agentId" label={t("dashboard.contacts.attrs.agentId")} rules={[validations.notNull]}>
                <AgentSelect />
              </Form.Item>
            </Col>
          </Row>
        ) : contactType === DashboardContactType.EXTERNAL ? (
          <>
            <Row gutter={rowGutter}>
              <Col span={12}>
                <Form.Item
                  name="name"
                  label={t("dashboard.contacts.attrs.name")}
                  rules={[validations.notBlank, validations.size(1, 255)]}
                >
                  <Input />
                </Form.Item>
              </Col>

              <Col span={12}>
                <InstitutionSelect
                  formItemProps={{
                    name: "institutionId",
                    label: t("dashboard.contacts.attrs.institutionId"),
                    rules: [validations.none],
                  }}
                  selectProps={{
                    allowClear: true,
                  }}
                />
              </Col>
            </Row>

            <Row gutter={rowGutter}>
              <Col span={12}>
                <Form.Item
                  name="email"
                  dependencies={["phone"]}
                  label={t("dashboard.contacts.attrs.email")}
                  rules={[
                    validations.size(1, 254),
                    validations.email,
                    validations.notNullIfOtherNull("phone", t("dashboard.contacts.attrs.phone")),
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  name="phone"
                  dependencies={["email"]}
                  label={t("dashboard.contacts.attrs.phone")}
                  rules={[
                    validations.size(1, 19),
                    validations.phoneNumber,
                    validations.notNullIfOtherNull("email", t("dashboard.contacts.attrs.email")),
                  ]}
                  getValueProps={(value) => ({ value: value ? formatPhoneNumber(value) : undefined })}
                  normalize={phoneNumberNormalizeFunction}
                >
                  <Input />
                </Form.Item>
              </Col>
            </Row>
          </>
        ) : undefined}
      </Form>
    </Modal>
  );
};
